<template>
  <transition name="fs-fade">
    <div v-if="localVisible" class="pcm-fullscreen">
      <!-- Header oscuro fullscreen -->
      <div class="pcm-header">
        <div class="pcm-header-left">
          <button class="pcm-back" @click="handleClose">
            <i class="el-icon-arrow-left"></i>
          </button>
          <h5 class="pcm-title">Cobros pendientes</h5>
          <span v-if="invoices.length" class="pcm-count-badge">{{
            invoices.length
          }}</span>
        </div>
        <div class="pcm-header-right">
          <button class="pcm-close" @click="handleClose">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="pcm-filters">
        <el-input
          v-model="search"
          placeholder="Buscar por cliente o codigo..."
          suffix-icon="el-icon-search"
          size="small"
          clearable
          class="pcm-search"
        />
        <div class="pcm-tabs">
          <button
            v-for="opt in statusOptions"
            :key="opt.value"
            :class="['pcm-tab', { active: filterStatus === opt.value }]"
            @click="filterStatus = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <el-select
          v-model="filterMinAmount"
          placeholder="Monto minimo"
          size="small"
          class="pcm-select"
        >
          <el-option label="Todos" :value="0" />
          <el-option label="+$100" :value="100" />
          <el-option label="+$500" :value="500" />
          <el-option label="+$1000" :value="1000" />
        </el-select>
      </div>

      <!-- Body scrollable -->
      <div class="pcm-body">
        <!-- Skeleton -->
        <div v-if="loading" class="pcm-skeleton">
          <div v-for="n in 8" :key="n" class="pcm-skeleton-row">
            <div class="sk-check"></div>
            <div class="sk-line w20"></div>
            <div class="sk-line w30"></div>
            <div class="sk-line w15"></div>
            <div class="sk-line w15"></div>
          </div>
        </div>

        <!-- Tabla -->
        <template v-else-if="invoices.length">
          <div class="pcm-table-header">
            <el-checkbox
              :indeterminate="isIndeterminate"
              :value="allSelected"
              @change="toggleSelectAll"
            />
            <span>Codigo</span>
            <span>Cliente</span>
            <span>Monto</span>
            <span>Estado</span>
            <span>Acciones</span>
          </div>

          <div
            v-for="inv in filteredInvoices"
            :key="inv.id"
            :class="[
              'pcm-table-row',
              { 'is-selected': selectedIds.includes(inv.id) }
            ]"
          >
            <el-checkbox
              :value="selectedIds.includes(inv.id)"
              @change="v => toggleSelect(inv.id, v)"
            />
            <a class="pcm-code-link" @click="goToInvoice(inv)">{{
              inv.code
            }}</a>
            <span class="pcm-client-text">{{ inv.client_name }}</span>
            <span class="pcm-amount-text">${{ formatMoney(inv.total) }}</span>
            <span>
              <span
                :class="[
                  'pcm-status-badge',
                  inv.is_overdue ? 'status-overdue' : 'status-ok'
                ]"
              >
                {{ inv.is_overdue ? "Vencida" : "Al dia" }}
              </span>
            </span>
            <div class="pcm-row-actions">
              <el-tooltip
                content="Enviar recordatorio"
                placement="top"
                effect="dark"
              >
                <button
                  type="button"
                  class="pcm-btn-action"
                  @click="rowSendReminder(inv)"
                  aria-label="Enviar recordatorio"
                >
                  <i class="el-icon-message"></i>
                </button>
              </el-tooltip>
              <el-tooltip
                content="Marcar como cobrada"
                placement="top"
                effect="dark"
              >
                <button
                  type="button"
                  class="pcm-btn-paid"
                  @click="markPaid(inv)"
                  aria-label="Marcar como cobrada"
                >
                  <i class="el-icon-check"></i>
                </button>
              </el-tooltip>
            </div>
          </div>

          <div v-if="!filteredInvoices.length" class="pcm-no-results">
            <i class="el-icon-search"></i>
            <span>Sin resultados con los filtros aplicados</span>
          </div>
        </template>

        <!-- Empty -->
        <div v-else class="pcm-empty">
          <i class="el-icon-circle-check"></i>
          <span>Todos los cobros al dia</span>
        </div>
      </div>

      <!-- Barra de acciones masivas -->
      <transition name="toolbar-slide">
        <div v-if="selectedIds.length" class="pcm-bulk-bar">
          <span class="pcm-bulk-info">
            {{ selectedIds.length }} factura{{
              selectedIds.length !== 1 ? "s" : ""
            }}
            seleccionada{{ selectedIds.length !== 1 ? "s" : "" }}
          </span>
          <div class="pcm-bulk-actions">
            <el-button
              size="small"
              class="pcm-bulk-btn"
              @click="openBulkReminder"
            >
              <i class="el-icon-chat-dot-round"></i> Recordatorio ({{
                selectedIds.length
              }})
            </el-button>
            <el-button
              size="small"
              class="pcm-bulk-btn-secondary"
              @click="bulkMarkPaid"
            >
              <i class="el-icon-check"></i> Marcar cobradas ({{
                selectedIds.length
              }})
            </el-button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import dashboardService from "../services/dashboardService";
import invoiceService from "../../sales/pages/invoices/services/invoiceService";

export default {
  name: "PendingCollectionModal",
  props: {
    visible: { type: Boolean, default: false },
    period: { type: String, default: "month" }
  },
  data() {
    return {
      loading: false,
      invoices: [],
      search: "",
      filterStatus: "all",
      filterMinAmount: 0,
      selectedIds: [],
      statusOptions: [
        { label: "Todas", value: "all" },
        { label: "Al dia", value: "ok" },
        { label: "Vencidas", value: "overdue" }
      ]
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    filteredInvoices() {
      let list = this.invoices;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          r =>
            (r.code || "").toLowerCase().includes(q) ||
            (r.client_name || "").toLowerCase().includes(q)
        );
      }
      if (this.filterStatus === "ok") list = list.filter(r => !r.is_overdue);
      else if (this.filterStatus === "overdue")
        list = list.filter(r => r.is_overdue);
      if (this.filterMinAmount > 0)
        list = list.filter(r => r.total >= this.filterMinAmount);
      return list;
    },
    allSelected() {
      return (
        this.filteredInvoices.length > 0 &&
        this.filteredInvoices.every(i => this.selectedIds.includes(i.id))
      );
    },
    isIndeterminate() {
      const count = this.filteredInvoices.filter(i =>
        this.selectedIds.includes(i.id)
      ).length;
      return count > 0 && count < this.filteredInvoices.length;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadInvoices();
        document.addEventListener("keydown", this.onEscape);
        document.body.style.overflow = "hidden";
      } else {
        document.removeEventListener("keydown", this.onEscape);
        document.body.style.overflow = "";
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
    document.body.style.overflow = "";
  },
  methods: {
    async loadInvoices() {
      this.loading = true;
      this.selectedIds = [];
      try {
        const res = await dashboardService.getPendingCollection({
          params: { period: this.period }
        });
        if (res.data && res.data.success) {
          this.invoices = res.data.data || [];
        }
      } catch (e) {
        this.$message.error("Error al cargar cobros pendientes");
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll(val) {
      if (val) {
        const ids = this.filteredInvoices.map(i => i.id);
        this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
      } else {
        const filteredIds = this.filteredInvoices.map(i => i.id);
        this.selectedIds = this.selectedIds.filter(
          id => !filteredIds.includes(id)
        );
      }
    },
    toggleSelect(id, val) {
      if (val) {
        if (!this.selectedIds.includes(id))
          this.selectedIds = [...this.selectedIds, id];
      } else {
        this.selectedIds = this.selectedIds.filter(i => i !== id);
      }
    },
    async markPaid(inv) {
      try {
        await this.$confirm(
          `Marcar factura ${inv.code} como cobrada?`,
          "Confirmar",
          {
            confirmButtonText: "Si, cobrada",
            cancelButtonText: "Cancelar",
            type: "info"
          }
        );
        await invoiceService.invoicePaid({
          user_business_invoice_id: inv.id,
          is_paid: true
        });
        this.invoices = this.invoices.filter(i => i.id !== inv.id);
        this.selectedIds = this.selectedIds.filter(id => id !== inv.id);
        this.$message.success(`Factura ${inv.code} marcada como cobrada`);
        this.$emit("invoices-updated");
      } catch {
        /* Cancelado */
      }
    },
    async bulkMarkPaid() {
      const count = this.selectedIds.length;
      const ids = [...this.selectedIds];
      try {
        await this.$confirm(
          `Marcar ${count} factura${count !== 1 ? "s" : ""} como cobrada${
            count !== 1 ? "s" : ""
          }?`,
          "Confirmar",
          {
            confirmButtonText: "Si, continuar",
            cancelButtonText: "Cancelar",
            type: "info"
          }
        );
        // Una sola request al backend (antes: N requests secuenciales).
        // 20 facturas: ~3s → ~200ms.
        const response = await invoiceService.invoicesBulkPaid(ids, true);
        if (!response || !response.data || !response.data.success) {
          this.$message.error("No se pudieron actualizar todas las facturas");
          return;
        }
        this.invoices = this.invoices.filter(i => !ids.includes(i.id));
        this.selectedIds = [];
        this.$message.success(
          `${count} factura${count !== 1 ? "s" : ""} cobrada${
            count !== 1 ? "s" : ""
          }`
        );
        this.$emit("invoices-updated");
      } catch {
        /* Cancelado */
      }
    },
    goToInvoice(inv) {
      this.$root.$emit("preview-invoice", inv.id);
    },
    rowSendReminder(inv) {
      this.$emit("send-reminder", {
        recipients: [
          {
            id: inv.client_id,
            name: inv.client_name,
            phone: inv.client_phone || null,
            email: inv.client_email || null,
            invoiceCode: inv.code,
            invoiceMonto: inv.total,
            invoiceDays: inv.is_overdue ? inv.days_overdue : 0
          }
        ],
        context: "invoices"
      });
    },
    openBulkReminder() {
      const recipients = this.filteredInvoices
        .filter(i => this.selectedIds.includes(i.id))
        .map(i => ({
          id: i.client_id,
          name: i.client_name,
          phone: i.client_phone || null,
          email: i.client_email || null,
          invoiceCode: i.code,
          invoiceMonto: i.total,
          invoiceDays: i.is_overdue ? i.days_overdue : 0
        }));
      this.$emit("send-reminder", { recipients, context: "invoices" });
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    },
    handleClose() {
      this.$emit("update:visible", false);
    },
    onEscape(e) {
      if (e.key === "Escape") this.handleClose();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$dark-header: #1e1c42;
$accent: #3ecfb4;
$border: #e4eaf6;
$bg: #f7f8f9;

// ── FULLSCREEN MODAL ──
.pcm-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Header limpio
.pcm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.pcm-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pcm-back {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}
.pcm-close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}
.pcm-title {
  font-size: 16px;
  font-weight: 600;
  color: $primary;
  margin: 0;
}
.pcm-count-badge {
  background: #534ab7;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.pcm-header-right {
  display: flex;
  align-items: center;
}

// Filtros
.pcm-filters {
  display: flex;
  gap: 12px;
  padding: 14px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  background: #fff;
}
.pcm-search {
  flex: 1;
  min-width: 180px;
  max-width: 320px;
}
.pcm-tabs {
  display: flex;
  border: 1px solid $border;
  border-radius: 6px;
  overflow: hidden;
}
.pcm-tab {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  background: #fff;
  border: none;
  cursor: pointer;
  border-right: 1px solid $border;
  transition: all 0.15s;
  &:last-child {
    border-right: none;
  }
  &:hover {
    background: $bg;
  }
  &.active {
    background: $primary;
    color: #fff;
  }
}
.pcm-select {
  width: 130px;
}

// Body
.pcm-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

// Table
.pcm-table-header,
.pcm-table-row {
  ::v-deep .el-checkbox {
    margin: 0;
    padding: 0;
    line-height: 1;
    .el-checkbox__label {
      display: none;
    }
  }
}
.pcm-table-header {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 100px 96px;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 2px solid $border;
  font-size: 12px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: sticky;
  top: 0;
  z-index: 5;
}
.pcm-table-row {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 100px 96px;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-bottom: 1px solid $border;
  font-size: 14px;
  min-height: 56px;
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
  &.is-selected {
    background: #f0f4ff;
  }
}
.pcm-code-text {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
}
.pcm-code-link {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #3ecfb4;
  }
}
.pcm-client-text {
  color: #303133;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pcm-amount-text {
  font-weight: 700;
  color: $primary;
  font-size: 14px;
}
.pcm-status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  &.status-ok {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.status-overdue {
    background: #fff0f0;
    color: #e24b4a;
  }
}
.pcm-row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pcm-btn-action,
.pcm-btn-paid {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  i {
    margin: 0;
  }
}
.pcm-btn-action {
  background: transparent;
  border: 1px solid $accent;
  color: $accent;
  &:hover {
    background: rgba($accent, 0.1);
    color: darken($accent, 8%);
  }
}
.pcm-btn-paid {
  background: transparent;
  border: 1px solid #1d9e75;
  color: #1d9e75;
  &:hover {
    background: rgba(#1d9e75, 0.1);
    color: darken(#1d9e75, 6%);
  }
}
.pcm-no-results {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 40px;
  color: #c0c4cc;
  font-size: 13px;
  justify-content: center;
}
.pcm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #c0c4cc;
  gap: 12px;
  i {
    font-size: 42px;
    color: $accent;
  }
  span {
    font-size: 14px;
    font-weight: 600;
    color: #909399;
  }
}

// Bulk bar
.pcm-bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}
.pcm-bulk-info {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.pcm-bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.pcm-bulk-btn {
  font-size: 12px;
  background: $accent;
  border: 1px solid $accent;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: darken($accent, 5%);
    border-color: darken($accent, 5%);
    color: #fff;
  }
}
.pcm-bulk-btn-secondary {
  font-size: 12px;
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
  border-radius: 8px;
  &:hover,
  &:focus {
    border-color: #909399;
    color: $primary;
  }
}

// Transitions
.toolbar-slide-enter-active,
.toolbar-slide-leave-active {
  transition: max-height 0.25s, opacity 0.25s;
  overflow: hidden;
  max-height: 80px;
}
.toolbar-slide-enter,
.toolbar-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fs-fade-enter-active,
.fs-fade-leave-active {
  transition: opacity 0.2s;
}
.fs-fade-enter,
.fs-fade-leave-to {
  opacity: 0;
}

// Skeleton
.pcm-skeleton {
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pcm-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sk-check {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.sk-line {
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  &.w15 {
    width: 15%;
  }
  &.w20 {
    width: 20%;
  }
  &.w30 {
    width: 30%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .pcm-table-header {
    display: none !important;
  }
  .pcm-table-row {
    display: flex !important;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 16px;
    font-size: 14px;

    > .el-checkbox {
      display: none;
    }

    .pcm-code-text {
      font-size: 13px;
      color: #909399;
    }
    .pcm-client-text {
      flex-basis: 100%;
      order: -2;
      font-size: 15px;
      font-weight: 600;
    }
    .pcm-amount-text {
      font-size: 15px;
    }
  }
  .pcm-row-actions {
    margin-left: auto;
    display: flex;
    gap: 8px;
  }
  .pcm-filters {
    flex-direction: column;
    gap: 8px;
    .pcm-search {
      max-width: 100%;
    }
    .pcm-tabs {
      width: 100%;
      overflow-x: auto;
    }
  }
}
</style>
